import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { FadeUp } from './Reveal';

const FEED_ID = process.env.REACT_APP_BEHOLD_FEED_ID;

const imageFor = (post) => {
    if (post?.sizes?.large?.mediaUrl) return post.sizes.large.mediaUrl;
    if (post?.sizes?.medium?.mediaUrl) return post.sizes.medium.mediaUrl;
    if (post?.mediaType === 'VIDEO' && post.thumbnailUrl) return post.thumbnailUrl;
    return post?.mediaUrl;
};

const truncate = (text, n = 220) => {
    if (!text) return '';
    if (text.length <= n) return text;
    return `${text.slice(0, n).trimEnd()}…`;
};

const InstagramPeek = () => {
    const { lang, t } = useLanguage();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!FEED_ID) return;
        let cancelled = false;
        fetch(`https://feeds.behold.so/${FEED_ID}`, { headers: { Accept: 'application/json' } })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                if (cancelled) return;
                const first = Array.isArray(data?.posts) ? data.posts[0] : null;
                setPost(first);
            })
            .catch(() => !cancelled && setError(true));
        return () => {
            cancelled = true;
        };
    }, []);

    if (!FEED_ID || error) return null;

    const caption = post?.prunedCaption || post?.caption || '';
    const timestamp = post?.timestamp
        ? new Date(post.timestamp).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : '';

    return (
        <section
            className="border-b border-white/10 bg-surface"
            data-testid="instagram-peek-section"
            aria-label={t.instagram.latest}
        >
            <div className="px-6 md:px-12 py-20 md:py-28 max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
                    <FadeUp className="md:col-span-6">
                        <a
                            href={post?.permalink || 'https://www.instagram.com/robinspencer.artist/?hl=en'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative block aspect-square overflow-hidden spotlight"
                            data-testid="instagram-peek-image-link"
                            aria-label={post?.altText || truncate(caption, 80) || t.instagram.latest}
                        >
                            {post ? (
                                <motion.img
                                    key={post.id}
                                    src={imageFor(post)}
                                    alt={post.altText || truncate(caption, 120) || t.instagram.latest}
                                    className="w-full h-full object-cover"
                                    initial={{ scale: 1.08, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                                    loading="lazy"
                                />
                            ) : (
                                <div className="w-full h-full bg-ink animate-pulse" data-testid="instagram-peek-skeleton" />
                            )}
                            {post?.mediaType === 'VIDEO' && (
                                <span className="absolute top-4 right-4 rounded-full bg-ink/70 backdrop-blur px-3 py-1 text-[10px] tracking-[0.25em] uppercase text-paper">
                                    Video
                                </span>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <ArrowUpRight className="absolute bottom-5 right-5 w-6 h-6 text-paper opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </a>
                    </FadeUp>

                    <div className="md:col-span-6 md:pl-6">
                        <FadeUp delay={0.1}>
                            <span
                                className="text-xs tracking-[0.35em] uppercase text-dusk flex items-center gap-3"
                                data-testid="instagram-peek-kicker"
                            >
                                <Instagram className="w-4 h-4" strokeWidth={1.5} />
                                {t.instagram.latest}
                                {timestamp && <span className="text-fog/70 normal-case tracking-normal">· {timestamp}</span>}
                            </span>
                        </FadeUp>
                        <FadeUp delay={0.18}>
                            <h2
                                className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-paper max-w-xl"
                                data-testid="instagram-peek-caption"
                            >
                                {post ? (
                                    <span>{truncate(caption, 180) || t.instagram.title}</span>
                                ) : (
                                    <span className="inline-block w-full h-24 bg-ink/60 animate-pulse rounded" />
                                )}
                            </h2>
                        </FadeUp>
                        <FadeUp delay={0.28}>
                            <a
                                href={post?.permalink || 'https://www.instagram.com/robinspencer.artist/?hl=en'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group mt-10 inline-flex items-center gap-3 rounded-full border border-white/25 px-6 py-3 text-sm text-paper transition-colors duration-300 hover:border-dusk hover:text-dusk"
                                data-testid="instagram-peek-cta"
                            >
                                {t.instagram.viewOnInstagram}
                                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </FadeUp>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InstagramPeek;
